/**
 * 成员列表：显示在场者、麦位状态与主持人操作。
 */

import { useMemo, useState } from 'react'

import { hasPerm, Perm, Rel, RoomFlag, UState } from '../lib/protocol'
import { useRoomStore, type Member } from '../stores/roomStore'
import { Avatar } from './Avatar'

export function MemberList() {
  const members = useRoomStore((s) => s.members)
  const selfId = useRoomStore((s) => s.selfId)
  const micQueue = useRoomStore((s) => s.micQueue)
  const speaking = useRoomStore((s) => s.speaking)
  const assignMic = useRoomStore((s) => s.assignMic)
  const releaseMic = useRoomStore((s) => s.releaseMic)
  const kick = useRoomStore((s) => s.kick)
  const setUserProp = useRoomStore((s) => s.setUserProp)
  const invitePM = useRoomStore((s) => s.invitePM)
  const renameUser = useRoomStore((s) => s.renameUser)
  const reportContent = useRoomStore((s) => s.reportContent)
  const moveToWaiting = useRoomStore((s) => s.moveToWaiting)
  // 用订阅而不是 getState()：getState() 拿的是调用那一刻的快照，
  // 房间标志位变了不会触发重渲染，开关等候室后这个按钮不会跟着出现/消失。
  const roomFlags = useRoomStore((s) => s.room?.flags ?? 0)
  const waitingOn = (roomFlags & RoomFlag.StateWaitingRoom) !== 0
  const ownerId = useRoomStore((s) => s.room?.ownerId ?? -2)
  const iAmOwner = ownerId === selfId
  const cancelPM = useRoomStore((s) => s.cancelPM)
  const pmPending = useRoomStore((s) => s.pmPending)
  const stopPM = useRoomStore((s) => s.stopPM)
  const setRelation = useRoomStore((s) => s.setRelation)
  const pmPeer = useRoomStore((s) => s.pmPeer)
  const relations = useRoomStore((s) => s.relations)

  const [menuFor, setMenuFor] = useState<number | null>(null)
  // 人多时靠肉眼找人很费劲，尤其是要在几十人里点某一个做操作。
  const [keyword, setKeyword] = useState('')

  const me = members.get(selfId)
  const canPM = !!me && hasPerm(me.prop, Perm.PrivateMsg)
  const canManageMic = !!me && hasPerm(me.prop, Perm.SetMic)
  const canKick = !!me && hasPerm(me.prop, Perm.KickUser)
  const canSetProp = !!me && hasPerm(me.prop, Perm.SetUserProp)
  // 联席主持要拿到整套管理权，不只是一个「主持」标签；
  // 服务端不许授予自己没有的位，所以按我自己有的裁一遍。
  const hostBits = HOST_BITS & (me?.prop ?? 0)

  // 排序：主持人在前，持麦者次之，其余按进房时间。
  const sorted = useMemo(() => {
    const kw = keyword.trim().toLowerCase()
    const list = [...members.values()].filter(
      (m) => !kw || (m.name || '').toLowerCase().includes(kw),
    )
    return list.sort((a, b) => {
      const rank = (m: Member) =>
        // 举手的人排最前：主持人要第一眼看到「谁想说话」。
        (m.state & UState.HandRaised) !== 0
          ? -1
          : hasPerm(m.prop, Perm.Op)
            ? 0
            : m.micId >= 0
              ? 1
              : 2
      const d = rank(a) - rank(b)
      return d !== 0 ? d : a.enterTime - b.enterTime
    })
  }, [members, keyword])

  // 当前在麦上的非主持人。主持人不算 —— 讲课的人被自己一键静音毫无道理。
  const onMicOthers = useMemo(
    () => [...members.values()].filter((m) => m.micId >= 0 && !hasPerm(m.prop, Perm.Op)),
    [members],
  )
  const onMicCount = onMicOthers.length

  /** 一键全体静音：把在麦的人挨个请下麦。 */
  const muteAll = () => {
    for (const m of onMicOthers) releaseMic(m.id)
  }

  return (
    <div className={`panel member-panel${menuFor !== null ? ' menu-open' : ''}`}>
      <div className="panel-head">
        <span>在场成员</span>
        <span className="badge">{members.size}</span>
        {canManageMic && onMicCount > 0 && (
          <button
            className="mini head-act"
            title="把所有人请下麦。主持人不受影响，否则一点就把自己也静音了。"
            onClick={muteAll}
          >
            全体静音（{onMicCount}）
          </button>
        )}
      </div>

      {/* 搜索框只在人多到肉眼找不过来时才出现，几个人的小课不必占地方。 */}
      {members.size > 8 && (
        <input
          className="member-search"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索成员"
        />
      )}

      {micQueue.length > 0 && (
        <div className="mic-queue">
          <div className="mic-queue-title">申请发言（{micQueue.length}）</div>
          <ol>
            {micQueue.map((id) => (
              <li key={id}>
                <span>{members.get(id)?.name ?? `席位 ${id}`}</span>
                {canManageMic && (
                  <button className="mini" onClick={() => assignMic(id)}>
                    给麦
                  </button>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      <ul className={`member-list${menuFor !== null ? ' menu-open' : ''}`}>
        {sorted.map((m) => {
          const isOp = hasPerm(m.prop, Perm.Op)
          const onMic = m.micId >= 0
          const level = speaking.get(m.id) ?? 0
          return (
            <li
              key={m.id}
              className={`member${m.id === selfId ? ' self' : ''}${onMic ? ' on-mic' : ''}`}
              onClick={() => setMenuFor(menuFor === m.id ? null : m.id)}
            >
              {/* 头像 + 说话指示环：腾讯会议靠头像认人，只有一个小圆点识别度太差。
                  说话时头像外圈发光，比单独一个点更容易注意到。 */}
              <span className={`member-avatar${level > 15 ? ' talking' : ''}`}>
                <Avatar name={m.name || `席位 ${m.id}`} size={28} />
              </span>
              <span className="member-name">
                {m.name || `席位 ${m.id}`}
                {m.id === selfId && <em className="tag-self">我</em>}
              </span>
              <span className="member-tags">
                {isOp && <em className="tag tag-op">{m.id === ownerId ? '主持' : '联席'}</em>}
                {(m.state & UState.HandRaised) !== 0 && <em className="tag tag-hand">✋ 举手</em>}
                {onMic && <em className="tag tag-mic">{m.micId + 1}麦</em>}
                {pmPeer === m.id && <em className="tag tag-secret">私聊中</em>}
                {muted(relations, m.id) && <em className="tag tag-away">已屏蔽</em>}
                {(m.state & UState.VideoOn) !== 0 && <em className="tag">视频</em>}
                {(m.state & UState.Leaving) !== 0 && <em className="tag tag-away">离开</em>}
              </span>

              {menuFor === m.id && m.id === selfId && (
                <div className="member-menu" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => promptRename(m.name, (n) => renameUser(m.id, n))}>改名…</button>
                </div>
              )}
              {menuFor === m.id && m.id !== selfId && (
                <div className="member-menu" onClick={(e) => e.stopPropagation()}>
                  {canPM &&
                    (pmPeer === m.id ? (
                      <button onClick={stopPM}>结束私聊</button>
                    ) : pmPending === m.id ? (
                      <button onClick={() => cancelPM(m.id)}>撤回邀请</button>
                    ) : (
                      <button onClick={() => invitePM(m.id)}>发起私聊</button>
                    ))}

                  <button
                    onClick={() =>
                      setRelation(m.id, Rel.NoReceiveText, !hasRel(relations, m.id, Rel.NoReceiveText))
                    }
                  >
                    {hasRel(relations, m.id, Rel.NoReceiveText) ? '取消屏蔽文字' : '屏蔽其文字'}
                  </button>
                  <button
                    onClick={() =>
                      setRelation(m.id, Rel.NoReceiveAudio, !hasRel(relations, m.id, Rel.NoReceiveAudio))
                    }
                  >
                    {hasRel(relations, m.id, Rel.NoReceiveAudio) ? '取消屏蔽语音' : '屏蔽其语音'}
                  </button>

                  {/* 举报入口：任意成员都能举报他人的不良内容/行为，落到后台由管理员处理。
                      这是 App Store 指南 1.2 对含实时互动应用的硬性要求。 */}
                  <button
                    onClick={() =>
                      promptReport((detail) =>
                        reportContent({
                          target: `席位 ${m.id}`,
                          targetNick: m.name,
                          category: 'user',
                          detail,
                        }),
                      )
                    }
                  >
                    举报此人…
                  </button>

                  {canManageMic &&
                    (onMic ? (
                      <button onClick={() => releaseMic(m.id)}>请他下麦</button>
                    ) : (
                      <button onClick={() => assignMic(m.id)}>
                        {(m.state & UState.HandRaised) !== 0 ? '允许他发言' : '给他麦'}
                      </button>
                    ))}
                  {/* 自由发言模式下发言者不占麦位，onMic 为假，所以单独给一个静音入口。 */}
                  {canManageMic && !onMic && (m.state & UState.Speaking) !== 0 && (
                    <button onClick={() => releaseMic(m.id)}>静音他</button>
                  )}
                  {canSetProp && (
                    <>
                      <button
                        onClick={() =>
                          setUserProp(
                            m.id,
                            Perm.Write,
                            hasPerm(m.prop, Perm.Write) ? 'remove' : 'add',
                          )
                        }
                      >
                        {hasPerm(m.prop, Perm.Write) ? '禁止发言' : '允许发言'}
                      </button>
                      <button
                        onClick={() =>
                          setUserProp(
                            m.id,
                            Perm.Whiteboard | Perm.OpWB,
                            hasPerm(m.prop, Perm.Whiteboard) ? 'remove' : 'add',
                          )
                        }
                      >
                        {hasPerm(m.prop, Perm.Whiteboard) ? '收回白板权' : '授予白板权'}
                      </button>
                      <button onClick={() => promptRename(m.name, (n) => renameUser(m.id, n))}>改名…</button>
                      {/* 联席之间互不能动，只有主持人能取消联席 —— 服务端同样的规则。 */}
                      {(!isOp || iAmOwner) && m.id !== ownerId && (
                        <button onClick={() => setUserProp(m.id, isOp ? HOST_BITS : hostBits, isOp ? 'remove' : 'add')}>
                          {isOp ? '取消联席主持' : '设为联席主持'}
                        </button>
                      )}
                      {iAmOwner && (
                        <button
                          title="对方成为主持人，你自己退为普通成员"
                          onClick={() => {
                            if (!window.confirm(`把主持人移交给 ${m.name || `席位 ${m.id}`}？你将退为普通成员。`)) return
                            setUserProp(m.id, hostBits, 'add')
                            setUserProp(selfId, HOST_BITS, 'remove')
                          }}
                        >
                          移交主持人
                        </button>
                      )}
                    </>
                  )}
                  {/* 移到等候室比直接踢人温和：他在门外排队，可以随后再放进来。
                      没开等候室时服务端会拒（没有落脚处），所以这里也不显示。 */}
                  {canKick && waitingOn && (
                    <button onClick={() => moveToWaiting(m.id)} title="他会回到等候室，你可以随后再允许他进来">
                      移到等候室
                    </button>
                  )}
                  {canKick && (
                    <button className="danger" onClick={() => kick(m.id)}>
                      请出房间
                    </button>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** 判断本机对某人是否设了指定关系位。 */
function hasRel(relations: Map<number, number>, id: number, bits: number): boolean {
  return ((relations.get(id) ?? 0) & bits) !== 0
}

/** 是否屏蔽了此人（文字或语音任一）。 */
function muted(relations: Map<number, number>, id: number): boolean {
  return hasRel(relations, id, Rel.NoReceiveText | Rel.NoReceiveAudio)
}

/** 弹个输入框改名。空值或没改动就当取消。 */
function promptRename(current: string, apply: (name: string) => void) {
  const next = window.prompt('新的显示名（最多 32 字）', current)
  if (next === null) return
  const n = next.trim()
  if (n && n !== current) apply(n)
}

/**
 * 弹出举报说明输入，确认后回调提交。
 * 说明可留空 —— 举报本身即有效，说明只是帮助管理员判断。
 */
function promptReport(apply: (detail: string) => void) {
  const detail = window.prompt(
    '举报原因（可留空）：如骚扰、不当言论、不良画面等。提交后由管理员处理。',
    '',
  )
  if (detail === null) return
  apply(detail.trim())
}

/** 主持人的一整套管理权限位。授予/收回联席主持、移交主持人时整组操作。 */
export const HOST_BITS =
  Perm.Op | Perm.KickUser | Perm.SetRoomProp | Perm.SetUserProp | Perm.SetOp |
  Perm.SetMic | Perm.SetMicNum | Perm.LockRoom | Perm.LockWB | Perm.DelWB |
  Perm.AppShare | Perm.Record | Perm.Broadcast
